import React, { useRef } from "react";
import { GsdReadableBlock } from "@/coquille-bridge";
import { getFormationIntroContent, getFormationIntroSteps } from "./karateFormationIntroContent.js";

/**
 * @param {object} props
 * @param {'admin'|'sensei'} props.trackId
 * @param {object} props.progress
 * @param {'fr'|'en'} props.locale
 * @param {boolean} [props.reviewMode]
 * @param {(start: boolean) => void | Promise<void>} props.onStartTraining
 */
export function KarateFormationIntroPage({ trackId, progress, locale, reviewMode = false, onStartTraining }) {
  const content = getFormationIntroContent(locale);
  const steps = getFormationIntroSteps(trackId, locale);
  const stepsRef = useRef(null);
  const hasProgress = (progress?.doneCount ?? 0) > 0;
  const startLabel = hasProgress ? (locale === "en" ? "Continue my training" : "Continuer ma formation") : content.startTraining;

  function scrollToSteps() {
    stepsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <section className="karate-formation-intro" data-testid="formation-intro-page">
      <header className="karate-formation-intro__hero">
        <h2>{content.title}</h2>
        <p className="karate-formation-intro__lead">{content.subtitle}</p>
        <p className="karate-formation-intro__reassurance">{content.reassurance}</p>
        <p className="karate-formation-intro__count" data-testid="formation-intro-step-count">
          {content.stepCountLabel(steps.length)}
        </p>
      </header>

      <div className="karate-formation-intro__actions">
        <button type="button" className="btn-primary" data-testid="formation-intro-start" onClick={() => onStartTraining(true)}>
          {startLabel}
        </button>
        <button type="button" className="btn-primary-outline" data-testid="formation-intro-view-steps" onClick={scrollToSteps}>
          {content.viewSteps}
        </button>
      </div>

      <GsdReadableBlock className="karate-formation-intro__path" testId="formation-intro-path">
        <h3>{trackId === "sensei" ? content.pathTitleSensei : content.pathTitleAdmin}</h3>
        <ol className="karate-formation-intro__steps" ref={stepsRef} id="formation-intro-steps" data-testid="formation-intro-steps">
          {steps.map((step) => (
            <li key={step.n} className="karate-formation-intro__step" data-testid={`formation-intro-step-${step.n}`}>
              <span className="karate-formation-intro__step-num" aria-hidden="true">
                {step.n}
              </span>
              <div>
                <strong>{step.title}</strong>
                <p>{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </GsdReadableBlock>

      <GsdReadableBlock className="karate-formation-intro__how" testId="formation-intro-how">
        <h3>{content.howTitle}</h3>
        <ul>
          {content.howItems.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </GsdReadableBlock>

      {reviewMode ? (
        <p className="muted">
          <button type="button" className="btn-primary-outline" data-testid="formation-intro-back-cockpit" onClick={() => onStartTraining(false)}>
            {locale === "en" ? "Back to my training cockpit" : "Retour au cockpit Formation"}
          </button>
        </p>
      ) : null}
    </section>
  );
}
